import { Combobox } from '@base-ui/react/combobox';
import { RiArrowDownSLine, RiCheckLine, RiCloseLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { useFieldControlProps } from './field';
import { renderIcon, type IconLike } from './icon';
import { usePortalContainer, type PortalContainer } from './lib/portal';
import { dropdownItem, dropdownSurface, popupMotion } from './lib/surfaces';

export interface MultiSelectOption<T extends string | number> {
  value: T;
  label: string;
  icon?: IconLike;
  disabled?: boolean;
}

export interface MultiSelectProps<T extends string | number> {
  options: readonly MultiSelectOption<T>[];
  value?: readonly T[];
  defaultValue?: readonly T[];
  onValueChange?: (value: T[]) => void;
  placeholder?: string;
  /** Shown in the list when the typed filter matches nothing. */
  emptyText?: React.ReactNode;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /** Accessible label of a chip's remove button; receives the option label. */
  removeLabel?: (label: string) => string;
  className?: string;
  popupClassName?: string;
  container?: PortalContainer;
}

/**
 * Multi-select field — kit 67:11292 in the product field style: White/10 box r8 (min-h32, padding 4/6, gap 4),
 * chips = Badge sm (White/10 + white, r4) with a remove ×, type-to-filter input, dropdown list with checks.
 * Built on Base UI Combobox (`multiple`).
 */
export function MultiSelect<T extends string | number>({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Tanlang',
  emptyText = 'Hech narsa topilmadi',
  disabled,
  invalid,
  required,
  name,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  removeLabel = (label) => `${label} — olib tashlash`,
  className,
  popupClassName,
  container,
}: MultiSelectProps<T>) {
  const field = useFieldControlProps({ id, disabled, required, invalid, 'aria-describedby': ariaDescribedBy });
  const portalContainer = usePortalContainer(container);
  const items = Combobox.createItems(options as MultiSelectOption<T>[], {
    getValue: (option) => option.value,
    getLabel: (option) => option.label,
  });
  const labelOf = (v: T) => options.find((o) => o.value === v)?.label ?? String(v);

  return (
    <Combobox.Root
      items={items}
      multiple
      value={value as T[] | undefined}
      defaultValue={defaultValue as T[] | undefined}
      onValueChange={(next) => onValueChange?.(next as T[])}
      disabled={field.disabled}
      required={field.required}
      name={name}
    >
      <Combobox.InputGroup
        className={cx(
          'group/field flex min-h-32 w-full min-w-0 cursor-text flex-wrap items-center gap-4 rounded-8 border border-transparent bg-field py-4 pr-8 pl-6',
          'transition-colors duration-150 focus-within:border-focus data-disabled:cursor-not-allowed data-disabled:opacity-60',
          field.invalid && 'border-tone-red-50 focus-within:border-tone-red',
          className,
        )}
      >
        <Combobox.Value>
          {(selected: T[]) => (
            <Combobox.Chips className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
              {selected.map((v) => (
                <Combobox.Chip
                  key={String(v)}
                  className="flex h-20 max-w-full items-center gap-2 rounded-4 bg-field pr-2 pl-6 text-xs font-semibold text-fg outline-none data-highlighted:bg-line-strong focus-within:bg-line-strong"
                >
                  <span className="truncate">{labelOf(v)}</span>
                  <Combobox.ChipRemove
                    aria-label={removeLabel(labelOf(v))}
                    className="flex size-16 shrink-0 cursor-pointer items-center justify-center rounded-4 text-icon hover:bg-hover hover:text-fg"
                  >
                    <RiCloseLine aria-hidden className="size-14" />
                  </Combobox.ChipRemove>
                </Combobox.Chip>
              ))}
              <Combobox.Input
                id={field.id}
                aria-label={ariaLabel}
                aria-describedby={field['aria-describedby']}
                aria-invalid={field.invalid || undefined}
                placeholder={selected.length ? '' : placeholder}
                className="h-22 min-w-48 flex-1 bg-transparent px-6 t-field text-fg outline-none placeholder:text-fg-secondary"
              />
            </Combobox.Chips>
          )}
        </Combobox.Value>
        <Combobox.Trigger
          aria-label="Ro'yxatni ochish"
          className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-4 text-icon hover:text-fg"
        >
          <RiArrowDownSLine aria-hidden className="size-16 transition-transform duration-150 group-data-popup-open/field:rotate-180" />
        </Combobox.Trigger>
      </Combobox.InputGroup>

      <Combobox.Portal container={portalContainer}>
        <Combobox.Positioner sideOffset={4} align="start" className="z-75 outline-none">
          <Combobox.Popup
            className={cn(
              dropdownSurface,
              popupMotion,
              'max-h-[min(var(--available-height),20rem)] w-(--anchor-width) overflow-y-auto overscroll-contain',
              popupClassName,
            )}
          >
            <Combobox.Empty className="px-8 py-6 text-13 text-fg-secondary empty:hidden">{emptyText}</Combobox.Empty>
            <Combobox.List className="flex flex-col gap-2">
              {(option: MultiSelectOption<T>) => (
                <Combobox.Item
                  key={String(option.value)}
                  value={option.value}
                  disabled={option.disabled}
                  className={cn(dropdownItem, 'gap-8 data-selected:text-fg')}
                >
                  {renderIcon(option.icon, 'size-16 text-icon')}
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  <Combobox.ItemIndicator className="flex shrink-0">
                    <RiCheckLine aria-hidden className="size-16 text-accent-2" />
                  </Combobox.ItemIndicator>
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
}
